import { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  { value: "09:00", label: "9:00 AM" },
  { value: "09:30", label: "9:30 AM" },
  { value: "10:30", label: "10:30 AM" },
  { value: "11:00", label: "11:00 AM" },
  { value: "13:30", label: "1:30 PM" },
  { value: "14:00", label: "2:00 PM" },
  { value: "15:00", label: "3:00 PM" },
  { value: "16:00", label: "4:00 PM" },
];

const BOOKING_FOR_OPTIONS = [
  "Myself (Participant)",
  "A Family Member / Loved One",
  "NDIS Support Coordinator (Participant Referral)",
];

const SERVICE_OPTIONS = [
  "Support Coordination",
  "Personal Care & Daily Living",
  "Community Access & Outings",
  "Respite & In-Home Support",
  "Household Tasks & Domestic Help",
  "Not sure yet — help me decide",
];

const inputClass =
  "w-full px-3.5 py-2.5 text-sm rounded-xl border border-[#D5CABB] bg-white text-[#242A2E] focus:outline-none focus:border-[#2E3C42] transition-colors";
const labelClass = "block text-xs font-medium text-[#48565D] mb-1";

export default function BookingSection() {
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [bookedTimes, setBookedTimes] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", email: "", booking_for: BOOKING_FOR_OPTIONS[0], service: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!date) return;
    const dateStr = format(date, "yyyy-MM-dd");
    setTime(null);
    axios
      .get(`${API}/bookings/availability`, { params: { date: dateStr } })
      .then((res) => setBookedTimes(res.data.booked_times || []))
      .catch(() => setBookedTimes([]));
  }, [date]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!date || !time) {
      toast.error("Please pick a date and a time slot first.");
      return;
    }
    if (!form.name.trim() || !form.phone.trim() || !form.service) {
      toast.error("Please add your name, phone number and the service you need.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        booking_for: form.booking_for,
        service: form.service,
        date: format(date, "yyyy-MM-dd"),
        time,
        notes: form.notes.trim() || null,
      });
      toast.success("Appointment booked — confirmation is on its way to you and our team.");
      setForm({ name: "", phone: "", email: "", booking_for: BOOKING_FOR_OPTIONS[0], service: "", notes: "" });
      setBookedTimes((b) => [...b, time]);
      setTime(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Could not complete the booking. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 px-5 sm:px-8 bg-[#F2ECE3] border-t border-[#E4DCCE]" data-testid="contact-section">
      <div className="max-w-6xl mx-auto space-y-12">
        <div className="text-center max-w-2xl mx-auto space-y-3">
          <span className="text-xs uppercase tracking-widest text-[#786655] font-semibold">Take The Next Step</span>
          <h2 className="text-3xl sm:text-4xl font-display font-medium text-[#1E282D]">Book an appointment or send an enquiry</h2>
          <p className="text-sm text-[#4E5B62]">
            Choose a self-scheduled time directly on our calendar, or send a brief message. You and our team will receive instant confirmation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 bg-[#FAF8F5] p-6 sm:p-8 rounded-3xl border border-[#E0D7C9] shadow-sm space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-[#EBE4D8]">
              <div>
                <h3 className="text-lg font-display font-medium text-[#1E282D]">1. Select a Date & Time</h3>
                <p className="text-xs text-[#637279]">Intake consultation, service inquiry, or coordinator intro</p>
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 bg-[#EAE0D2] text-[#4E3E30] rounded-full">Mon – Sat</span>
            </div>

            <div className="flex justify-center" data-testid="booking-calendar">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(d) => d < today || d.getDay() === 0}
                classNames={{
                  day_selected: "bg-[#2E3C42] text-white hover:bg-[#2E3C42] hover:text-white focus:bg-[#2E3C42] focus:text-white rounded-full",
                  day_today: "bg-[#EFE7DC] text-[#1E282D] rounded-full",
                }}
              />
            </div>

            <div className="space-y-3">
              <div className="text-xs font-medium uppercase tracking-wider text-[#637279]">Select Time Slot (ACST / Adelaide)</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="time-slot-grid">
                {TIME_SLOTS.map((slot) => {
                  const isBooked = bookedTimes.includes(slot.value);
                  const isSelected = time === slot.value;
                  return (
                    <button
                      key={slot.value}
                      type="button"
                      disabled={!date || isBooked}
                      onClick={() => setTime(slot.value)}
                      data-testid={`time-slot-${slot.value.replace(":", "")}`}
                      className={`py-2.5 px-3 text-xs font-medium rounded-lg border transition-colors ${
                        isSelected
                          ? "border-[#3E4C53] bg-[#2E3C42] text-white"
                          : isBooked
                            ? "border-[#EBE4D8] bg-[#F4EFE6] text-[#B4A9B9] line-through cursor-not-allowed"
                            : "border-[#DFD6C9] bg-white text-[#334046] hover:bg-[#F4ECE0] disabled:opacity-50 disabled:cursor-not-allowed"
                      }`}
                    >
                      {slot.label}
                    </button>
                  );
                })}
              </div>
              {!date && <p className="text-[11px] text-[#718087]">Pick a date on the calendar to see available times.</p>}
            </div>

            <form className="space-y-4 pt-2 border-t border-[#EBE4D8]" onSubmit={submitBooking} data-testid="booking-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} htmlFor="booking-name">Your Full Name</label>
                  <input id="booking-name" data-testid="booking-name-input" type="text" placeholder="First and last name" className={inputClass}
                    value={form.name} onChange={(e) => setField("name", e.target.value)} />
                </div>
                <div>
                  <label className={labelClass} htmlFor="booking-phone">Phone Number</label>
                  <input id="booking-phone" data-testid="booking-phone-input" type="tel" placeholder="04XX XXX XXX" className={inputClass}
                    value={form.phone} onChange={(e) => setField("phone", e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass} htmlFor="booking-email">Email (optional, for confirmation)</label>
                  <input id="booking-email" data-testid="booking-email-input" type="email" placeholder="you@example.com" className={inputClass}
                    value={form.email} onChange={(e) => setField("email", e.target.value)} />
                </div>
                <div>
                  <label className={labelClass}>Service Needed</label>
                  <Select value={form.service} onValueChange={(v) => setField("service", v)}>
                    <SelectTrigger data-testid="booking-service-select" className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-[#D5CABB] bg-white text-[#334046]">
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`service-option-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className={labelClass}>I am booking on behalf of</label>
                <Select value={form.booking_for} onValueChange={(v) => setField("booking_for", v)}>
                  <SelectTrigger data-testid="booking-for-select" className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-[#D5CABB] bg-white text-[#334046]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BOOKING_FOR_OPTIONS.map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={labelClass} htmlFor="booking-notes">Anything we should know? (optional)</label>
                <textarea id="booking-notes" data-testid="booking-notes-input" rows="2" placeholder="Access needs, preferences, questions..." className={inputClass}
                  value={form.notes} onChange={(e) => setField("notes", e.target.value)} />
              </div>
              <button type="submit" disabled={submitting} data-testid="booking-submit-button"
                className="w-full py-3.5 px-6 rounded-xl font-medium text-sm text-[#FAF8F5] bg-[#223036] hover:bg-[#141E22] transition-colors shadow-sm disabled:opacity-60">
                {submitting ? "Booking..." : "Confirm Appointment & Notify Our Team"}
              </button>
              <p className="text-[11px] text-[#718087] text-center">You'll receive an instant confirmation, and our team is notified straight away. We never share your details.</p>
            </form>
          </div>

          <EnquiryForm />
        </div>
      </div>
    </section>
  );
}

function EnquiryForm() {
  const [form, setForm] = useState({ name: "", contact: "", services: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submitEnquiry = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contact.trim() || !form.message.trim()) {
      toast.error("Please add your name, how to reach you, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        contact: form.contact.trim(),
        services: form.services.trim() || null,
        message: form.message.trim(),
      });
      toast.success("Enquiry sent — our team will get back to you today.");
      setForm({ name: "", contact: "", services: "", message: "" });
    } catch (err) {
      toast.error("Could not send your enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="lg:col-span-5 bg-[#FAF8F5] p-6 sm:p-8 rounded-3xl border border-[#E0D7C9] shadow-sm flex flex-col justify-between space-y-6">
      <div className="space-y-2">
        <h3 className="text-lg font-display font-medium text-[#1E282D]">2. Or Send a Quick Message</h3>
        <p className="text-xs text-[#5C6A71]">Prefer writing to us first? Drop your questions and we will respond today.</p>
      </div>

      <form className="space-y-4" onSubmit={submitEnquiry} data-testid="enquiry-form">
        <div>
          <label className={labelClass} htmlFor="enquiry-name">Your Name</label>
          <input id="enquiry-name" data-testid="enquiry-name-input" type="text" placeholder="Name" className={inputClass}
            value={form.name} onChange={(e) => setField("name", e.target.value)} />
        </div>
        <div>
          <label className={labelClass} htmlFor="enquiry-contact">Email or Phone</label>
          <input id="enquiry-contact" data-testid="enquiry-contact-input" type="text" placeholder="How should we reach you?" className={inputClass}
            value={form.contact} onChange={(e) => setField("contact", e.target.value)} />
        </div>
        <div>
          <label className={labelClass} htmlFor="enquiry-services">Services Needed</label>
          <input id="enquiry-services" data-testid="enquiry-services-input" type="text" placeholder="e.g. Daily care, household help, coordination" className={inputClass}
            value={form.services} onChange={(e) => setField("services", e.target.value)} />
        </div>
        <div>
          <label className={labelClass} htmlFor="enquiry-message">Message / Notes</label>
          <textarea id="enquiry-message" data-testid="enquiry-message-input" rows="3" placeholder="Tell us about what you are looking for..." className={inputClass}
            value={form.message} onChange={(e) => setField("message", e.target.value)} />
        </div>
        <button type="submit" disabled={submitting} data-testid="enquiry-submit-button"
          className="w-full py-3.5 px-6 rounded-xl font-medium text-sm text-[#FAF8F5] bg-[#3B4D55] hover:bg-[#2A373D] transition-colors shadow-sm disabled:opacity-60">
          {submitting ? "Sending..." : "Send Quick Enquiry"}
        </button>
      </form>

      <div className="p-4 rounded-2xl bg-[#F4EFE6] border border-[#E4DCCE] text-xs text-[#526066] space-y-1" data-testid="direct-contact-card">
        <div className="font-semibold text-[#242E33]">Direct Contact · Beneficence Care</div>
        <div>
          Call or text: <a href="tel:+61450552557" data-testid="contact-phone-link" className="underline hover:text-[#1E282D] transition-colors">0450 552 557</a>
        </div>
        <div>
          Email: <a href="mailto:ndis@beneficencecare.com" data-testid="contact-email-link" className="underline hover:text-[#1E282D] transition-colors">ndis@beneficencecare.com</a>
        </div>
        <div>Serving Metropolitan Adelaide & South Australia Statewide</div>
      </div>
    </div>
  );
}
